import type { ComponentProps } from "solid-js";
import { mergeProps, Match, Show, Switch } from "solid-js";
import Text from "../../text/Text";
import { useTranslation } from "../../../state/i18n";
import { IconButton } from "../../button/Button";
import { Icon } from "../../icon/Icon";
import TextSelectValue from "../selectValues/TextSelectValue";
import IconSelectValue from "../selectValues/IconSelectValue";
import ImageSelectValue from "../selectValues/ImageSelectValue";
import DynamicValueSelectValue from "../selectValues/DynamicValueSelectValue";
import type { SelectOption } from "../../../types/Menu";
import { narrow } from "../../../utils/solidUtils";

type SingleSelectTriggerProps<T> = {
  ariaLabel: string;
  value: SelectOption<T> | null | undefined;
  withClearButton: boolean;
  placeholder?: string;
  onClear?: () => void;
  hasError: boolean;
  disabled?: boolean;
  arkProps: Omit<ComponentProps<"button">, "disabled">;
};

const SingleSelectTrigger = <T,>(rawProps: SingleSelectTriggerProps<T>) => {
  const props = mergeProps({ disabled: false }, rawProps);

  let triggerRef: HTMLButtonElement | undefined;
  const { t } = useTranslation();

  return (
    <div role="group" class="relative">
      <button
        ref={triggerRef}
        aria-label={props.ariaLabel}
        disabled={props.disabled}
        class="group flex h-9.5 w-full items-center rounded-sm border py-2 pr-1 pl-2 text-start focus-visible:outline-default"
        classList={{
          "cursor-pointer text-primary": !props.disabled,
          "border-disabled bg-surface-disabled text-disabled": props.disabled,
          "border-error bg-surface-destructive": props.hasError && !props.disabled,
          "border-default bg-surface-default hover:bg-surface-hover": !props.hasError && !props.disabled,
        }}
        {...props.arkProps}
      >
        <div class="flex w-full min-w-0" classList={{ "pr-8.5": props.withClearButton }}>
          <Show
            when={props.value}
            fallback={
              <Text
                as="span"
                truncate
                text={props.placeholder ?? t(($) => $.SELECT_INPUT_PLACEHOLDER_DEFAULT)}
                class="text-base-medium text-placeholder select-none"
              />
            }
          >
            {(option) => (
              <Switch>
                <Match when={narrow(option(), (o) => o.variant === "image")}>
                  {(o) => <ImageSelectValue {...o()} />}
                </Match>
                <Match when={narrow(option(), (o) => o.variant === "icon")}>
                  {(o) => <IconSelectValue {...o()} />}
                </Match>
                <Match when={narrow(option(), (o) => o.variant === "text" || o.variant === "avatar")}>
                  {(o) => <TextSelectValue {...o()} />}
                </Match>
                <Match when={narrow(option(), (o) => o.variant === "dynamicValue")}>
                  {(o) => <DynamicValueSelectValue {...o()} />}
                </Match>
              </Switch>
            )}
          </Show>
        </div>
        <div class="flex size-7 shrink-0 items-center justify-center">
          <Icon
            type="solid"
            name="chevronDown"
            size="md"
            class="shrink-0 icon-secondary group-disabled:icon-disabled"
          />
        </div>
      </button>
      <Show when={props.withClearButton}>
        <div class="pointer-events-none absolute inset-y-0 right-8 flex items-center pl-1">
          <IconButton
            aria-label={t(($) => $.SELECT_SINGLE_CLEAR)}
            title={t(($) => $.SELECT_SINGLE_CLEAR)}
            size="medium"
            variant="tertiary"
            icon="x"
            onClick={() => {
              props.onClear?.();
              triggerRef?.focus();
            }}
            class="pointer-events-auto"
          />
        </div>
      </Show>
    </div>
  );
};

export default SingleSelectTrigger;
